import type { FigureLayout } from '#shared/core/card-geometry';
import { INK } from './backdrops';

/**
 * The built-in mannequin: a faceless standing figure with a ball on the hip, shown until the
 * player has real card art. It is drawn the way a baked sticker looks (white body, ink outline,
 * grape hard shadow) and placed by the same fitFigure rule, so a mannequin card and an art card
 * line up. Coordinates are in its own sticker space (218 x 482) with the figure box at (14,14).
 */

const PAD = 14;
const FIG_W = 190;
const FIG_H = 454;
const OUTLINE = 7;
const SHADOW = [6, 4] as const;

export const MANNEQUIN_LAYOUT: FigureLayout = {
  stickerBox: [PAD, PAD, FIG_W, FIG_H],
  stickerW: FIG_W + PAD * 2,
  stickerH: FIG_H + PAD * 2,
};

/** Body parts in figure-box coordinates (0..190 by 0..454). The head is drawn separately. */
const BODY = [
  // neck
  'M85,52L105,52L106,80L84,80Z',
  // torso
  'M46,86Q95,68 144,86L137,196Q135,214 140,236L50,236Q55,214 53,196Z',
  // left arm, hanging, with a hand
  'M46,88L27,94L17,198L13,258L37,262L42,204L56,132Z',
  // right arm, bent to the ball
  'M144,88L163,94L178,170L160,214L140,188Z',
] as const;
const ACCENT_PARTS = [
  // shorts
  'M50,224L140,224L150,306L100,306L95,276L90,306L40,306Z',
  // shoes
  'M50,428L96,428L102,446Q104,454 96,454L48,454Q42,454 44,446Z',
  'M94,428L140,428L148,446Q150,454 142,454L90,454Q86,454 88,446Z',
] as const;
const LEGS = ['M52,300L94,300L90,432L58,432Z', 'M96,300L138,300L132,432L100,432Z'] as const;

export interface MannequinProps {
  /** Shorts and shoes. Default lavender. */
  accent?: string;
  /** Solid ink instead of white: the reveal's silhouette state. */
  silhouette?: boolean;
  shadowColor: string;
}

/** The mannequin in sticker space; the caller wraps it in the fitFigure transform. */
export function Mannequin({ accent = '#e2a4ff', silhouette = false, shadowColor }: MannequinProps) {
  const base = silhouette ? INK : '#ffffff';
  const acc = silhouette ? INK : accent;
  const all = [...BODY, ...LEGS, ...ACCENT_PARTS];
  return (
    <g transform={`translate(${PAD} ${PAD})`} strokeLinejoin="round">
      {/* hard shadow: every part offset, outline and fill in grape */}
      <g
        transform={`translate(${SHADOW[0]} ${SHADOW[1]})`}
        fill={shadowColor}
        stroke={shadowColor}
        strokeWidth={OUTLINE * 2}
      >
        {all.map((d) => (
          <path key={`s${d}`} d={d} />
        ))}
        <ellipse cx={95} cy={29} rx={25} ry={29} />
        <circle cx={160} cy={238} r={26} />
      </g>
      {/* ink outline of the union: strokes first, fills after, so inner seams vanish */}
      <g fill={INK} stroke={INK} strokeWidth={OUTLINE * 2}>
        {all.map((d) => (
          <path key={`o${d}`} d={d} />
        ))}
        <ellipse cx={95} cy={29} rx={25} ry={29} />
        <circle cx={160} cy={238} r={26} />
      </g>
      <g stroke="none">
        {[...BODY, ...LEGS].map((d) => (
          <path key={`f${d}`} d={d} fill={base} />
        ))}
        {ACCENT_PARTS.map((d) => (
          <path key={`a${d}`} d={d} fill={acc} />
        ))}
        <ellipse cx={95} cy={29} rx={25} ry={29} fill={base} />
      </g>
      {/* seams between shorts and legs, and the ball */}
      {!silhouette && (
        <g fill="none" stroke={INK} strokeWidth={3} strokeLinecap="round">
          <path d="M95,276L95,306" />
          <path d="M46,236L144,236" />
        </g>
      )}
      <g>
        <circle cx={160} cy={238} r={26} fill={silhouette ? INK : '#ffc299'} stroke={INK} strokeWidth={5} />
        {!silhouette && (
          <g fill="none" stroke={INK} strokeWidth={3} strokeLinecap="round">
            <path d="M160,212L160,264" />
            <path d="M134,238L186,238" />
            <path d="M143,220Q152,238 143,256" />
            <path d="M177,220Q168,238 177,256" />
          </g>
        )}
      </g>
      {!silhouette && (
        <text
          x={95}
          y={43}
          textAnchor="middle"
          fontFamily="'Fira Sans', 'Arial Black', sans-serif"
          fontWeight={900}
          fontStyle="italic"
          fontSize={42}
          fill={INK}
        >
          ?
        </text>
      )}
    </g>
  );
}
